import { useCallback, useMemo, useState } from "react";
import { useRequestTimelapseVideo, useTimelapseVideoStatus } from "@visioni/a3s-media/app";
import { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import type { TimelapseExportState } from "../types";
import type { TimelapseRange } from "../utils/date-range";
import { deriveExportState } from "./derive-export-state";

export interface UseTimelapseExportParams {
  readonly siteId: string;
  readonly cameraKey: string;
  readonly cameraTimezone: string;
  readonly range: TimelapseRange;
  readonly enabled?: boolean;
}

export interface TimelapseExportApi {
  readonly state: TimelapseExportState;
  /** Server-evaluated camera eligibility derived from status endpoint access. */
  readonly cameraEligible: boolean;
  /**
   * Trigger the asynchronous export. Idempotent: if a job already exists for
   * the same camera + window the server reuses it, so the user can hit the
   * download button repeatedly without queueing duplicate work.
   */
  readonly requestExport: () => Promise<void>;
  /**
   * Returned only when the job is `ready` — useful for native code paths that
   * download to the camera roll vs. the web "open in new tab" path.
   */
  readonly downloadUrl: string | null;
  readonly error: Error | null;
}

/**
 * Composes the request mutation and the status poller into a single
 * discriminated state for the UI.
 *
 * The status poller starts immediately so the UI can rehydrate an in-flight
 * job (e.g. user navigates away and comes back) without having to reissue
 * the POST. The POST itself only fires when the user clicks Download.
 */
export function useTimelapseExport(params: UseTimelapseExportParams): TimelapseExportApi {
  const { siteId, cameraKey, cameraTimezone, range, enabled = false } = params;

  const parsedSiteId = siteId ? SiteId.fromString(siteId) : undefined;
  const parsedCameraKey = cameraKey ? AssetCameraKey.fromString(cameraKey) : undefined;

  const pathParams = useMemo(
    () =>
      parsedSiteId && parsedCameraKey ?
        ({ siteId: parsedSiteId, assetCameraKey: parsedCameraKey } as const)
      : ({ siteId: undefined, assetCameraKey: undefined } as const),
    [parsedSiteId, parsedCameraKey],
  );

  const status = useTimelapseVideoStatus({
    ...pathParams,
    startTime: range.startTime,
    endTime: range.endTime,
    cameraTimezone,
    enabled: enabled && Boolean(siteId && cameraKey),
  });

  const request = useRequestTimelapseVideo(pathParams);

  // App-level "we have asked the server" flag — distinguishes "user clicked
  // before any job existed" from "no job has ever been requested for this
  // window". Status 404 on its own can't tell us that. Critically, only set
  // this *after* the POST resolves successfully: a failed mutation must not
  // promote the UI to `queued`, otherwise the button locks at "Queued" with
  // no way for the user to retry without leaving the page.
  const [hasRequested, setHasRequested] = useState(false);

  // Pull the stable callback identities out of the hook results — the
  // result objects themselves are fresh every render, but the underlying
  // hooks (`useRequestTimelapseVideo`, `useTimelapseVideoStatus`) now
  // pin their callbacks behind refs so these references survive rerenders.
  // Depending on them individually keeps `requestExport` stable, which in
  // turn keeps camera-page timelapse action handlers stable.
  const { requestVideo } = request;
  const { refetch: refetchStatus } = status;

  const requestExport = useCallback(async () => {
    // Awaited POST: throws on network/server failure. The error propagates
    // up to caller-level handlers that surface an export-request failure
    // toast. Crucially, `setHasRequested` only fires on
    // success — a failed POST must keep the UI in `idle` so the button
    // stays actionable for retry.
    await requestVideo({
      startTime: range.startTime,
      endTime: range.endTime,
      cameraTimezone,
    });
    setHasRequested(true);
    refetchStatus();
  }, [requestVideo, refetchStatus, range.startTime, range.endTime, cameraTimezone]);

  const state = useMemo<TimelapseExportState>(
    () => deriveExportState({ job: status.job, isRequesting: request.isPending, hasRequested }),
    [status.job, request.isPending, hasRequested],
  );

  return {
    state,
    cameraEligible: enabled && !status.isLoading && !status.isForbidden && status.error === null,
    requestExport,
    downloadUrl: state.kind === "ready" ? state.downloadUrl : null,
    error: status.error ?? request.error,
  };
}

export { deriveExportState, type DeriveExportStateInput } from "./derive-export-state";
